import client from '@config/client'
import API_URL from '@config/services'
import { ApiClientResult } from '@/types/api'
import queryString from '@utils/queryString'

interface TaskPayload {
  title: string
  description?: string
  due_date?: string
  priority?: string
  status?: string
  assigned_to?: number | string
  project_id?: number | string
  list_id?: number | string
  [key: string]: any
}

interface TaskParams {
  project_id?: number | string
  list_id?: number | string
  status?: string
  assigned_to?: number | string
  [key: string]: any
}

/**
 * Creates a new task
 * @param payload - Task data
 * @returns API response
 */
export const onCreateTask = (payload: TaskPayload): Promise<ApiClientResult> =>
  client
    .post('tasks/', payload)
    .then(response => response)
    .catch(error => {
      return error.response
    })

/**
 * Fetches tasks with optional filtering
 * @param params - Query parameters
 * @returns API response with tasks list
 */
export const onFetchTasks = (
  params: TaskParams = {}
): Promise<ApiClientResult> =>
  client
    .get(
      Object.keys(params).length > 0
        ? 'tasks/' + '?' + queryString(params)
        : 'tasks/'
    )
    .then(response => response)
    .catch(error => {
      return error.response
    })

/**
 * Updates a task
 * @param taskId - Task ID
 * @param payload - Task data to update
 * @returns API response
 */
export const onEditTask = (
  taskId: number | string,
  payload: Partial<TaskPayload> = {}
): Promise<ApiClientResult> =>
  client
    .patch(`tasks/${taskId}/`, payload)
    .then(response => response)
    .catch(error => error.response)

/**
 * Deletes a task
 * @param taskId - Task ID
 * @returns API response
 */
export const onDeleteTask = (
  taskId: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(`tasks/${taskId}/`)
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Logs tasks for an organization
 * @param orgId - Organization ID
 * @returns API response
 */
export const onLogTasks = (orgId: number | string): Promise<ApiClientResult> =>
  client
    .post(`tasks/log-tasks/${orgId}/`)
    .then(response => response)
    .catch(error => error.response)

/**
 * Fetches task lists
 * @param id - Project or organization ID
 * @returns API response with lists
 */
export const onFetchLists = (id: number | string): Promise<ApiClientResult> => {
  return client
    .get(`tasks/lists/${id ? `?${id}` : ''}`)
    .then(response => response)
    .catch(error => {
      return error.response
    })
}
